import { Show } from "solid-js";
import { Title } from "@solidjs/meta";

import { Divider } from "~/common/divider";
import { PrintLayout } from "~/common/print_layout";
import { formatLongMonthYear } from "~/util/format";
import {
  PhoneBreakdownTable,
  type PhoneBreakdownTableProps,
} from "~/billing/phone_breakdown_table";

type Props = PhoneBreakdownTableProps & {
  practiceName: string;
  windowStart: string;
  invoiceNumber: string | null;
  voided: boolean;
};

const SUPPORT_EMAIL = "support@twelve.ai";
const CARRIER_NOTE =
  "Phone service is provided in partnership with DialStack as the carrier of record.";
const DISCLOSURE_NOTE =
  "This is a disclosure of your phone service for the month. It is not a bill — there is nothing to pay here: your phone service is part of what you pay Twelve, and your Twelve invoice shows what you pay.";

function Meta(props: { label: string; value: string }) {
  return (
    <div class="flex justify-end gap-4">
      <dt class="text-typography-secondary print:text-black">{props.label}</dt>
      <dd class="min-w-32 text-right font-medium tabular-nums">{props.value}</dd>
    </div>
  );
}

export default function PhoneBreakdownShow(props: Props) {
  const period = () => formatLongMonthYear(props.windowStart);

  return (
    <>
      <Title>
        {[
          props.practiceName,
          "Phone service disclosure",
          period(),
          props.invoiceNumber ?? "Draft",
        ].join(", ")}
      </Title>

      <div class="min-h-screen bg-white text-sm text-typography-primary print-page-portrait print:min-h-0 print:bg-transparent print:px-[0.35in] print:text-black">
        <PrintLayout showTwelveLogo>
          <header class="flex flex-wrap items-start justify-between gap-6 pb-6">
            <div class="flex flex-col gap-1">
              <h1 class="text-xl font-semibold">Phone service disclosure</h1>
              <p class="text-base">{props.practiceName}</p>
              <Show when={!props.voided}>
                <p class="max-w-md pt-2 text-typography-secondary print:text-black">
                  {DISCLOSURE_NOTE}
                </p>
              </Show>
            </div>
            <dl class="flex flex-col gap-1">
              <Meta label="Service period" value={period()} />
              <Meta label="Invoice" value={props.invoiceNumber ?? "Draft"} />
            </dl>
          </header>
          <Divider />

          <Show
            when={!props.voided}
            fallback={<p class="py-8 text-base">This invoice was voided</p>}
          >
            <PhoneBreakdownTable {...props} />
          </Show>

          <Divider />
          <footer class="flex flex-col gap-1 pt-4 text-typography-secondary print:text-black">
            <p>{CARRIER_NOTE}</p>
            <p>Questions about this disclosure? {SUPPORT_EMAIL}</p>
          </footer>
        </PrintLayout>
      </div>
    </>
  );
}
